import {cloneDeep} from 'lodash';
import {IFood, IFoodSchedule} from '../food-group';
import {IRecordNutritionalHistory} from './record-nutritional-history.interface';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {environment} from '../../../environments/environment';
import {IAllergy} from './allergy.interface';
import {firstValueFrom} from 'rxjs';

export interface IRNutritionalHistory {
  recordNutritionalHistory: IRecordNutritionalHistory;
  wasDeployed?: boolean;
}

export namespace RecordNutritionalHistoryAction {
  export class GetActive {
    static readonly type = '[Record] Get Record Nutritional History Action';
    constructor(public serviceId: string, public patientId: string, public expertId: string) { }
  }

  export class PutActive {
    static readonly type = '[Record] Put Record Nutritional History Action';
    constructor(public serviceId: string, public patientId: string, public expertId: string, public recordNutritionalHistory: Omit<IRecordNutritionalHistory, '_id' | 'serviceId' | 'patientId' | 'expertId' | 'allergies' | 'cereals' | 'drinks' | 'additionalHighFatFoods' | 'foodSchedules'>) { }
  }

  export class PostAllergy {
    static readonly type = '[Record] Post Allergy Action';
    constructor(public serviceId: string, public patientId: string, public expertId: string, public allergy: IAllergy) { }
  }
  export class DeleteAllergyById {
    static readonly type = '[Record] Delete Allergy Like Action';
    constructor(public serviceId: string, public patientId: string, public expertId: string, public allergyId: string) { }
  }
  export class ResetState {
    static readonly type = '[Record] Reset Record Nutritional History Habits Action';
    constructor() { }
  }

  export class PutFoodSchedule {
    static readonly type = '[Record] Put Food Schedule Action';
    constructor(public serviceId: string, public patientId: string, public expertId: string, public data: IFoodSchedule) { }
  }

  export class PutCereal {
    static readonly type = '[Record] Put Cereal Action';
    constructor(public serviceId: string, public patientId: string, public expertId: string, public data: IFood) { }
  }

  export class PutBeverages {
    static readonly type = '[Record] Put Beverages Action';
    constructor(public serviceId: string, public patientId: string, public expertId: string, public data: IFood) { }
  }

  export class PutDressings {
    static readonly type = '[Record] Put Dressings Action';
    constructor(public serviceId: string, public patientId: string, public expertId: string, public data: IFood) { }
  }

  export class Deploy {
    static readonly type = '[Record] Was Deployed Record Nutritional History Action';
    constructor(public value: boolean) { }
  }
}

@State<IRNutritionalHistory>({
  name: 'NutritionalHistoryState',
  defaults: {
    recordNutritionalHistory: null
  },
})

@Injectable()
export class NutritionalHistoryState {
  constructor(
    private baseService: BaseService<any>,
  ) {
  }

  url = environment.SERVER_URL;

  @Selector()
  public static getRecordNutritionalHistory(state: IRNutritionalHistory): IRecordNutritionalHistory {
    return state.recordNutritionalHistory;
  }

  @Selector()
  public static getWasDeployed(state: IRNutritionalHistory): boolean {
    return state.wasDeployed;
  }

  @Action(RecordNutritionalHistoryAction.GetActive)
  async getRecordNutritionalHistoryAction(
    ctx: StateContext<IRNutritionalHistory>,
    action: RecordNutritionalHistoryAction.GetActive
  ): Promise<void> {
    const response: DataBaseServiceResponse<IRecordNutritionalHistory> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/nutritional-history/active`)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      recordNutritionalHistory: response.entity
    });
  }

  @Action(RecordNutritionalHistoryAction.PutActive)
  async putRecordNutritionalHistoryAction(
    ctx: StateContext<IRNutritionalHistory>,
    action: RecordNutritionalHistoryAction.PutActive
  ): Promise<void> {
    // TODO: Hay una función en lodash que puede quitar las propiedades que están nulas
    for (const prop in action.recordNutritionalHistory) {
      if (action.recordNutritionalHistory[prop] === null) {
        delete action.recordNutritionalHistory[prop];
      }
    }

    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.put(
        `${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/nutritional-history`,
        action.recordNutritionalHistory
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordNutritionalHistoryAction.PostAllergy)
  async postAllergyAction(
    ctx: StateContext<IRNutritionalHistory>,
    action: RecordNutritionalHistoryAction.PostAllergy
  ): Promise<void> {
    const response: DataBaseServiceResponse<IAllergy> = await firstValueFrom(
      this.baseService.post(
        `${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/nutritional-history/allergy`,
        action.allergy
      )
    );

    if (response.error) throw new Error(response.message);

    const record: IRecordNutritionalHistory = cloneDeep(ctx.getState().recordNutritionalHistory);
    if (record.allergies) {
      record.allergies.push(response.entity);
    } else {
      record.allergies = [response.entity];
    }

    ctx.patchState({
      recordNutritionalHistory: record
    });
  }

  @Action(RecordNutritionalHistoryAction.DeleteAllergyById)
  async deleteAllergyAction(
    ctx: StateContext<IRNutritionalHistory>,
    action: RecordNutritionalHistoryAction.DeleteAllergyById
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.delete(
        `${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/nutritional-history/allergy/${action.allergyId}`
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordNutritionalHistoryAction.ResetState)
  async resetRecordNutritionalHistoryAction(
    ctx: StateContext<IRNutritionalHistory>,
    _: RecordNutritionalHistoryAction.ResetState
  ): Promise<void> {
    ctx.patchState({
      recordNutritionalHistory: null
    });
  }

  @Action(RecordNutritionalHistoryAction.PutFoodSchedule)
  async putFoodScheduleAction(
    ctx: StateContext<IRNutritionalHistory>,
    action: RecordNutritionalHistoryAction.PutFoodSchedule
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.put(
        `${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/nutritional-history/schedules`,
        action.data
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordNutritionalHistoryAction.PutCereal)
  async putCerealAction(
    ctx: StateContext<IRNutritionalHistory>,
    action: RecordNutritionalHistoryAction.PutCereal
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.put(
        `${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/nutritional-history/cereals`,
        action.data
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordNutritionalHistoryAction.PutBeverages)
  async putBeveragesAction(
    ctx: StateContext<IRNutritionalHistory>,
    action: RecordNutritionalHistoryAction.PutBeverages
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.put(
        `${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/nutritional-history/drinks`,
        action.data
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordNutritionalHistoryAction.PutDressings)
  async putDressingsAction(
    ctx: StateContext<IRNutritionalHistory>,
    action: RecordNutritionalHistoryAction.PutDressings
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.put(
        `${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/nutritional-history/additional-high-fat-foods`,
        action.data
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordNutritionalHistoryAction.Deploy)
  async WasDeployedRecordNutritionalHistoryAction(
    ctx: StateContext<IRNutritionalHistory>,
    action: RecordNutritionalHistoryAction.Deploy
  ): Promise<void> {
    ctx.patchState({
      wasDeployed: action.value
    });
  }
}
